/**
 * O que a skill exige e o que ela pergunta, na forma que o formulário edita
 * (AB#85634).
 *
 * Em módulo próprio, e não junto dos passos: arquivo que exporta função ao lado
 * de componente quebra o hot reload do React — a mesma razão pela qual o
 * vocabulário do assistente de instalação mora em `types.ts`.
 *
 * As duas declarações moram no `SKILL.md` e são escritas de volta nele na hora
 * de publicar: é o que mantém o pacote portátil — o mesmo arquivo levado a
 * outro host continua exigindo e perguntando o mesmo.
 *
 * O que é lido por PESSOAS — rótulo, ajuda, motivo — é escrito por idioma
 * (AC37). O arquivo leva UM texto, o inglês, ou o português quando não houver
 * inglês, para o campo nunca faltar lá; o Prodgy guarda todos os idiomas e as
 * telas leem no de quem está olhando.
 *
 * Lista vazia não é lacuna: é a skill dizendo que não exige integração nenhuma
 * ou não pergunta nada, e é isso que faz a etapa correspondente **sumir** do
 * assistente de instalação.
 */
import type { IntegrationDeclaration, ParameterDeclaration } from "@prodgy/skills";

/**
 * Um identificador de LINHA, que não é o conteúdo dela.
 *
 * O `id` do requisito e a `key` do parâmetro são digitados por quem edita e
 * começam vazios — usá-los como chave de lista do React faz duas linhas novas
 * compartilharem a mesma chave, e remover uma do meio faz o React reaproveitar
 * o estado da linha errada. O índice tem o mesmo defeito na remoção.
 *
 * Então cada linha carrega uma marca própria, gerada uma vez e nunca exibida.
 */
export const novaLinha = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `linha-${Math.random().toString(36).slice(2)}`;

/** Um requisito enquanto está sendo editado — texto solto, ainda não normalizado. */
export type RequisitoEmEdicao = {
  /** Identidade da LINHA na tela; não vai para o `SKILL.md`. */
  uid: string;
  id: string;
  anyOf: string[];
  /** O motivo por idioma. O arquivo recebe o inglês, ou o português sem ele. */
  reason: Record<string, string>;
};

/** Um parâmetro enquanto está sendo editado. `options` é a lista crua do campo. */
export type ParametroEmEdicao = {
  /** Identidade da LINHA na tela; não vai para o `SKILL.md`. */
  uid: string;
  key: string;
  type: "text" | "enum" | "number" | "boolean";
  options: string;
  required: boolean;
  default: string;
  /** Rótulo e ajuda por idioma, pela mesma razão do motivo do requisito. */
  label: Record<string, string>;
  help: Record<string, string>;
};

/**
 * O texto que vai para o ARQUIVO.
 *
 * Inglês primeiro: pacote de skill é material aberto, e é em inglês que ele
 * circula. Sem inglês, vale o que houver — o arquivo nunca sai sem o campo,
 * porque um rótulo faltando quebraria a leitura em qualquer outro host.
 */
export const paraOArquivo = (porIdioma: Record<string, string>): string =>
  porIdioma.en?.trim() || Object.values(porIdioma).find((texto) => texto?.trim())?.trim() || "";

/** Prefixo que distingue uma FAMÍLIA de integrações de um template só. */
export const CLASSIFICACAO = "classification:";

/**
 * Lê do frontmatter o que já está declarado, para o formulário abrir com o que
 * o pacote trouxe em vez de com uma lista em branco.
 */
export function requisitosDoPacote(
  frontmatter: Record<string, unknown>,
  emTextoPlano: string[],
  /**
   * Em que idioma o texto do arquivo entra no formulário.
   *
   * O `SKILL.md` não diz em que idioma foi escrito, e quem registra é quem
   * sabe. Cai no inglês, que é como pacote aberto costuma vir.
   */
  idiomaDoArquivo = "en",
): RequisitoEmEdicao[] {
  const bloco = frontmatter.integrations;

  if (bloco && typeof bloco === "object" && !Array.isArray(bloco)) {
    return Object.entries(bloco as Record<string, unknown>).map(([id, bruto]) => {
      const campos = (bruto ?? {}) as Record<string, unknown>;
      const declarado = campos["any-of"] ?? campos.anyOf ?? campos.any_of;
      const formas = Array.isArray(declarado)
        ? declarado.map(String)
        : typeof declarado === "string"
          ? [declarado]
          : [];

      return {
        uid: novaLinha(),
        id,
        anyOf: formas.flatMap((forma) => forma.split("|")).map((forma) => forma.trim()),
        reason:
          typeof campos.reason === "string" && campos.reason.trim()
            ? { [idiomaDoArquivo]: campos.reason }
            : {},
      };
    });
  }

  // O acervo antigo declara em texto plano dentro de `metadata`; cada token vira
  // um requisito, com as alternativas que ele já trazia separadas por barra.
  return emTextoPlano.map((token) => ({
    uid: novaLinha(),
    id: token,
    anyOf: token.split("|").map((forma) => forma.trim()),
    reason: {},
  }));
}

export function parametrosDoPacote(
  frontmatter: Record<string, unknown>,
  idiomaDoArquivo = "en",
): ParametroEmEdicao[] {
  const bloco = frontmatter.parameters;
  if (!bloco || typeof bloco !== "object" || Array.isArray(bloco)) return [];

  return Object.entries(bloco as Record<string, unknown>).map(([key, bruto]) => {
    const campos = (bruto ?? {}) as Record<string, unknown>;
    const opcoes = Array.isArray(campos.enum) ? campos.enum.map(String) : [];
    const declarado = typeof campos.type === "string" ? campos.type.toLowerCase() : "";

    return {
      uid: novaLinha(),
      key,
      type:
        opcoes.length > 0
          ? ("enum" as const)
          : declarado === "number" || declarado === "integer"
            ? ("number" as const)
            : declarado === "boolean"
              ? ("boolean" as const)
              : ("text" as const),
      options: opcoes.join(", "),
      required: campos.required === true,
      default: campos.default === undefined || campos.default === null ? "" : String(campos.default),
      label:
        typeof campos.title === "string" && campos.title.trim()
          ? { [idiomaDoArquivo]: campos.title }
          : {},
      help:
        typeof campos.description === "string" && campos.description.trim()
          ? { [idiomaDoArquivo]: campos.description }
          : {},
    };
  });
}

/** O formato que o `SKILL.md` recebe — sem os campos vazios do formulário. */
export function paraDeclaracoes(
  requisitos: RequisitoEmEdicao[],
  parametros: ParametroEmEdicao[],
): { integrations: IntegrationDeclaration[]; parameters: ParameterDeclaration[] } {
  return {
    integrations: requisitos
      .filter((requisito) => requisito.id.trim() && requisito.anyOf.length > 0)
      .map((requisito) => ({
        id: requisito.id,
        anyOf: requisito.anyOf,
        reason: paraOArquivo(requisito.reason) || null,
      })),
    parameters: parametros
      .filter((parametro) => parametro.key.trim())
      .map((parametro) => ({
        key: parametro.key,
        type: parametro.type,
        options:
          parametro.type === "enum"
            ? parametro.options
                .split(",")
                .map((opcao) => opcao.trim())
                .filter(Boolean)
            : [],
        required: parametro.required,
        default: parametro.default.trim() || null,
        label: paraOArquivo(parametro.label) || null,
        help: paraOArquivo(parametro.help) || null,
      })),
  };
}

/**
 * Os mesmos textos, mas TODOS os idiomas, para o Prodgy guardar.
 *
 * O arquivo leva um; as telas leem estes. Idioma sem texto é descartado — mapa
 * vazio diria que alguém traduziu para nada.
 */
export function paraTraducoes(
  requisitos: RequisitoEmEdicao[],
  parametros: ParametroEmEdicao[],
): {
  integrations: Record<string, Record<string, { reason?: string }>>;
  parameters: Record<string, Record<string, { label?: string; help?: string }>>;
} {
  const porIdioma = <T extends Record<string, string>>(
    fontes: Record<string, string>[],
    campos: string[],
  ): Record<string, T> => {
    const idiomas = new Set(fontes.flatMap((fonte) => Object.keys(fonte)));
    const saida: Record<string, T> = {};

    for (const idioma of idiomas) {
      const entradas = campos
        .map((campo, indice) => [campo, fontes[indice]?.[idioma]?.trim()] as const)
        .filter((entrada): entrada is readonly [string, string] => !!entrada[1]);

      if (entradas.length > 0) saida[idioma] = Object.fromEntries(entradas) as T;
    }

    return saida;
  };

  const integrations: Record<string, Record<string, { reason?: string }>> = {};
  for (const requisito of requisitos) {
    if (!requisito.id.trim()) continue;

    const mapa = porIdioma<{ reason?: string }>([requisito.reason], ["reason"]);
    if (Object.keys(mapa).length > 0) integrations[requisito.id] = mapa;
  }

  const parameters: Record<string, Record<string, { label?: string; help?: string }>> = {};
  for (const parametro of parametros) {
    if (!parametro.key.trim()) continue;

    const mapa = porIdioma<{ label?: string; help?: string }>(
      [parametro.label, parametro.help],
      ["label", "help"],
    );
    if (Object.keys(mapa).length > 0) parameters[parametro.key] = mapa;
  }

  return { integrations, parameters };
}
